import React, { FC, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import {
  Beneficiary,
  Contract,
  ContractTenant,
  FeatureFlag,
  UserMetadata,
} from '../../../common/validations'
import { ContractOperation } from '../../../common/types'
import { TerminateContractForm } from './TerminateForm/TerminateContractForm'
import { ContractTransferForm } from './ContractTransferForm/ContractTransferForm'
import { TerminateContractErrorHandler } from './TerminateContractErrorHandler'
import { ContractModificationErrorHandler } from '../ContractChanges/ContractModificationErrorHandler'
import { ContractChangeTypeChoice } from '../ContractChanges/components/ContractChangeTypeChoice/ContractChangeTypeChoice'
import { ContractChangesViewState } from '../ContractChanges/types'
import { canContinueWithRemainingTenant } from '../ContractChanges/utils/canContinueWithRemainingTenant'

import { useModuleConfig } from 'hooks/useModuleConfig/useModuleConfig'
import { useContractPaths } from 'hooks/useContractPaths/useContractPaths'

type TerminationSituation = 'wholeContract' | 'contractContinues'

interface FormProps {
  view: ContractChangesViewState
  setView: (view: ContractChangesViewState) => void
  tenants: ContractTenant[]
  beneficiaries: Beneficiary[]
  contract: Contract
  userMetadata: UserMetadata
  featureFlags?: FeatureFlag
}

export const Form: FC<FormProps> = ({
  view,
  setView,
  tenants,
  beneficiaries,
  contract,
  userMetadata,
  featureFlags,
}) => {
  const { t } = useTranslation(['terminateContract', 'common'])
  const { contractModification } = useModuleConfig()
  const { terminateContractPath } = useContractPaths()
  const [params] = useSearchParams()
  const [terminateTenants, setTerminateTenants] = useState<number[]>([])
  const [tenantSelectionConfirmed, setTenantSelectionConfirmed] =
    useState(false)

  const canContinueContract = canContinueWithRemainingTenant(contract, tenants)

  const [situation, setSituation] = useState<TerminationSituation | undefined>(
    () => {
      // The strong authentication callback reloads the page, so the selected
      // situation is gone and the completed operation has to be read from the URL.
      switch (params.get('operation')) {
        case ContractOperation.MODIFICATION:
          return 'contractContinues'
        case ContractOperation.TERMINATION:
          return 'wholeContract'
        default:
          return canContinueContract && !params.has('error')
            ? undefined
            : 'wholeContract'
      }
    }
  )

  if (!contractModification) {
    return (
      <TerminateContractErrorHandler view="form">
        <TerminateContractForm
          view={view}
          setView={setView}
          tenantSelection={{
            type: 'perTenant',
            terminateTenants,
            setTerminateTenants,
            tenantSelectionConfirmed,
            setTenantSelectionConfirmed,
          }}
          tenants={tenants}
          beneficiaries={beneficiaries}
          contract={contract}
          userMetadata={userMetadata}
          featureFlags={featureFlags}
        />
      </TerminateContractErrorHandler>
    )
  }

  const resetSituation = () =>
    setSituation(canContinueContract ? undefined : 'wholeContract')

  const showChoice =
    canContinueContract && view === 'form' && !params.has('error')

  return (
    <>
      {showChoice && (
        <ContractChangeTypeChoice<TerminationSituation>
          heading={t('terminateContract:headings.terminationSituation')}
          options={[
            {
              id: 'wholeContract',
              label: t('terminateContract:terminationSituations.wholeContract'),
              description: t(
                'terminateContract:terminationSituations.wholeContractDescription'
              ),
              disabled: false,
            },
            {
              id: 'contractContinues',
              label: t(
                'terminateContract:terminationSituations.contractContinues'
              ),
              description: t(
                'terminateContract:terminationSituations.contractContinuesDescription'
              ),
              disabled: false,
            },
          ]}
          value={situation}
          onChange={setSituation}
        />
      )}

      {situation === 'wholeContract' && (
        <TerminateContractErrorHandler view="form">
          <TerminateContractForm
            view={view}
            setView={setView}
            tenantSelection={{ type: 'allTenants', onCancel: resetSituation }}
            tenants={tenants}
            beneficiaries={beneficiaries}
            contract={contract}
            userMetadata={userMetadata}
            featureFlags={featureFlags}
          />
        </TerminateContractErrorHandler>
      )}

      {situation === 'contractContinues' && (
        <ContractModificationErrorHandler
          view="form"
          restartPath={terminateContractPath}
        >
          <ContractTransferForm
            view={view}
            setView={setView}
            contract={contract}
            tenants={tenants}
            userMetadata={userMetadata}
            onCancel={resetSituation}
          />
        </ContractModificationErrorHandler>
      )}
    </>
  )
}
